/**
 * Protected Route Component
 * Redirects unauthenticated users to sign-in page
 */

import { useUser } from '@clerk/clerk-react';
import { useLocation } from 'wouter';
import { ReactNode, useEffect } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  redirectTo = '/sign-in'
}: ProtectedRouteProps) {
  const { isLoaded, isSignedIn } = useUser();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      // Redirect to sign-in if not authenticated
      const returnUrl = `${redirectTo}?returnTo=${encodeURIComponent(location)}`;
      setLocation(returnUrl);
    }
  }, [isLoaded, isSignedIn, location, redirectTo, setLocation]);

  // Show loading state while Clerk loads
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[var(--vault-black)] via-[var(--navy)] to-[var(--navy)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--brand)] mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render content if not signed in (will redirect via useEffect)
  if (!isSignedIn) {
    return null;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}
